<template>
  <div class="row mt-4">
    <div class="col">
      <button
        class="btn btn-secondary btn-sm mb-2"
        type="button"
        @click="loadCallsAsync"
        :disabled="refreshing"
        title="Osveži"
      >
        ⟳ Osveži
      </button>
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-2">
        <h5 class="mb-0">Klici</h5>
        <div class="d-flex align-items-center">
          <span class="text-muted small me-3" v-if="lastRefresh">
            Osveženo: {{ $dayjs(lastRefresh).format($DATETIME_FORMAT_DISPLAY) }}
          </span>
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="calls-auto-refresh" v-model="autoRefresh" />
            <label class="form-check-label" for="calls-auto-refresh">
              Samodejno osveževanje
              <span class="text-nowrap" v-if="autoRefresh">(čez {{ secondsToRefresh }} s)</span>
              <span class="text-nowrap" v-else>({{ autoRefreshSeconds }} s)</span>
            </label>
          </div>
        </div>
      </div>
      <div class="row mb-2">
        <div class="col-lg-5 mb-2">
          <div class="row">
            <label class="col-sm-3 col-form-label pt-0">Naša številka</label>
            <div class="col-sm-9">
              <div class="d-flex flex-wrap gap-3 align-items-center">
                <div class="form-check form-check-inline">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="calls_target"
                    id="calls_target_all"
                    value=""
                    v-model="filters.targetNumber"
                  />
                  <label class="form-check-label" for="calls_target_all">Vse</label>
                </div>
                <div class="form-check form-check-inline" v-for="n in OUR_NUMBERS" :key="n">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="calls_target"
                    :id="'calls_target_' + n"
                    :value="n"
                    v-model="filters.targetNumber"
                  />
                  <label class="form-check-label" :for="'calls_target_' + n">{{ n }}</label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-4 mb-2">
          <div class="row">
            <label class="col-sm-3 col-form-label">Klicatelj</label>
            <div class="col-sm-9">
              <input
                type="text"
                class="form-control"
                v-model.lazy="filters.callerNumber"
                placeholder="del številke ..."
              />
            </div>
          </div>
        </div>
        <div class="col-lg-3 mb-2 text-end">
          <button class="btn btn-secondary btn-sm" type="button" @click="resetFilters">Pobriši filtre</button>
        </div>
      </div>
      <div class="table-responsive">
        <table class="table table-stripped table-sm table-hover">
          <thead>
            <tr>
              <th scope="col">Naša številka</th>
              <th scope="col">Klicatelj</th>
              <th scope="col">Razlog</th>
              <th scope="col">Stanje</th>
              <th scope="col">Začetek</th>
              <th scope="col">Trajanje</th>
              <th scope="col">Preusmerjeno na</th>
            </tr>
          </thead>
          <tbody>
            <tr
              role="button"
              v-for="c in calls"
              :key="c.ID"
              :class="rowClass(c)"
              @click="$router.push({ name: 'CallEdit', params: { callId: c.ID } })"
            >
              <td>{{ c.TargetNumber }}</td>
              <td>{{ c.CallerNumber }}</td>
              <td :title="callReasonTitle(c)">
                <span v-if="callReason(c)">{{ callReason(c) }}</span>
                <span v-else class="text-danger small">⚠ brez razloga</span>
              </td>
              <td>{{ c.CallState }}</td>
              <td :title="c.CallStart ? $dayjs(c.CallStart).fromNow() : ''">
                {{ c.CallStart ? $dayjs(c.CallStart).format($DATETIME_FORMAT_DISPLAY) : "" }}
              </td>
              <td :title="c.CallEnd ? $dayjs(c.CallEnd).format($DATETIME_FORMAT_DISPLAY) : ''">
                {{ callDuration(c) }}
              </td>
              <td>
                {{ c.RedirectNumber
                }}<span v-if="c.RedirectReason" class="text-muted small"> ({{ c.RedirectReason }})</span>
              </td>
            </tr>
            <tr v-if="!calls?.length">
              <td colspan="7" class="text-center">Ni klicev.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <Paging v-if="calls?.length" :paging="paging" @change="setPageNum" />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import dayjs from "dayjs"
import { useAuth } from "../composition-api/useAuth"
import { listPagedCallsAsync } from "../api"
import { CALL_OUTCOMES } from "../utils/constants"
import { usePaging } from "../components/usePaging"

defineOptions({ name: "Calls" })

const autoRefreshSeconds = 15
const NEW_CALL_MINUTES = 15
const OUR_NUMBERS = ["56201260", "56201261"]

const { fetchAuthAsync } = useAuth()
const router = useRouter()
const route = useRoute()

const calls = ref([])
const autoRefresh = ref(localStorage.getItem("callsAutoRefresh") === "true")
const lastRefresh = ref(null)
const refreshing = ref(false)
const secondsToRefresh = ref(autoRefreshSeconds)
let ticker = null

// the filters live in the URL (like the other lists), so a filtered list can be reloaded/shared
const filters = reactive({
  targetNumber: route.query.targetNumber || "",
  callerNumber: route.query.callerNumber || "",
})

const buildFilterQuery = () => {
  let filter = ""
  if (filters.targetNumber) filter += `&targetNumber=${encodeURIComponent(filters.targetNumber)}`
  if (filters.callerNumber.trim()) filter += `&callerNumber=${encodeURIComponent(filters.callerNumber.trim())}`
  return filter
}

// The page is kept, so the auto-refresh (and the manual Osveži) reload what is on screen
// instead of throwing the operator back to the first page.
const loadCallsAsync = async () => {
  if (refreshing.value) return
  refreshing.value = true
  try {
    const result = await listPagedCallsAsync(fetchAuthAsync, paging.value, buildFilterQuery())
    calls.value = result.entities || []
    setNumberOfRecords(result.numberOfRecords || 0)
    lastRefresh.value = new Date()
  } finally {
    refreshing.value = false
    // a manual refresh restarts the countdown as well
    secondsToRefresh.value = autoRefreshSeconds
  }
}

const { paging, resetPaging, setPageNum, setNumberOfRecords } = usePaging(loadCallsAsync)

watch(
  () => ({ ...filters }),
  (value) => {
    const query = { ...route.query }

    if (value.targetNumber) query.targetNumber = value.targetNumber
    else delete query.targetNumber

    if (value.callerNumber.trim()) query.callerNumber = value.callerNumber.trim()
    else delete query.callerNumber

    // update the URL without reloading, then go back to the first page of the new filter
    router.replace({ query }).catch(() => {})
    resetPaging()
  },
)

const resetFilters = () => {
  filters.targetNumber = ""
  filters.callerNumber = ""
}

// A call that just came in is looked at first; one nobody has written a reason for has not
// been handled at all. Both get a row tint - Bootstrap's table-* classes colour a whole row
// (unlike a bg-* utility, which the cells' own background would hide).
const isNewCall = (call) => !!call?.DateCreated && dayjs().diff(dayjs(call.DateCreated), "minute") < NEW_CALL_MINUTES

// "not processed" = the operator has not said yet what the call was about (CallOutcome is null)
const isUnprocessed = (call) => !(call?.CallOutcome || "").trim()

const rowClass = (call) => {
  if (isNewCall(call)) return "table-warning"
  return isUnprocessed(call) ? "table-danger" : ""
}

// The human readable length of the call, e.g. "2 minuti" (empty while it is still
// ringing/active is shown as "v teku" and the exact end time stays in the cell title).
const callDuration = (call) => {
  if (!call?.CallStart) return ""
  if (!call?.CallEnd) return "v teku"
  const seconds = dayjs(call.CallEnd).diff(dayjs(call.CallStart), "second")
  return seconds > 0 ? dayjs.duration(seconds, "seconds").humanize() : ""
}

// "What was the call about" as the operator entered it: the outcome plus a snippet of the
// note, so an annotated call can be recognised without opening it. The full note (and the
// name of the caller) is in the cell title.
const callReason = (call) => {
  const outcome = CALL_OUTCOMES.find((o) => o.value === call?.CallOutcome)?.text || ""
  const note = (call?.Note || "").trim()
  const snippet = note.length > 60 ? `${note.slice(0, 60)}…` : note
  if (outcome && snippet) return `${outcome}: ${snippet}`
  return outcome || snippet
}

const callReasonTitle = (call) => [call?.CallerName, call?.Note].filter(Boolean).join(" - ")

const stopTicker = () => {
  if (ticker) {
    clearInterval(ticker)
    ticker = null
  }
}

// One 1 s ticker drives both the countdown next to the switch and the refresh itself.
const startTicker = () => {
  stopTicker()
  secondsToRefresh.value = autoRefreshSeconds
  if (!autoRefresh.value) return
  ticker = setInterval(() => {
    if (refreshing.value) return
    secondsToRefresh.value -= 1
    if (secondsToRefresh.value <= 0) {
      loadCallsAsync()
    }
  }, 1000)
}

watch(autoRefresh, (value) => {
  localStorage.setItem("callsAutoRefresh", JSON.stringify(value))
  startTicker()
})

onMounted(async () => {
  // resetPaging() marks the paging dirty, which triggers loadCallsAsync
  resetPaging()
  startTicker()
})

onUnmounted(stopTicker)
</script>
